import { Component, computed, inject, input, signal } from "@angular/core"
import { LngLat, Story } from "redlineLibs"
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons"
import { EditorService } from "../services/editor.service"
import { ConfirmNewMapLocationDialogComponent } from "../../shared/dialogs/confirm-new-map-location.dialog.component"
import { RouteService } from "../../../services/route.service"
import { UiIconComponent } from "ui-icon"
import { EditorLocationComponent } from "./editor-location.component"
import { UiInheritDirective } from "ui-inherit"
import { MapGoogleComponent } from "../../map/map-google.component"
import { regionsData } from "redlineLibs"

@Component({
  imports: [
    UiIconComponent,
    ConfirmNewMapLocationDialogComponent,
    EditorLocationComponent,
    UiInheritDirective,
    MapGoogleComponent,
  ],
  standalone: true,
  template: `
      <div class="h-[400px] relative">
        <e2e-map-google
          [showMarkers]="true"
          [story]="story()"
          [storyZoomLevel]="zoomLevel()"
          [markerLocation]="newMarkerLocation()"
          [regionDatum]="regionDatum()"
          (mapClick)="changeMarkerLocation($event)"
          (zoomLevelChange)="setZoomLevel($event)"
        />

        @if (confirmingChange()) {
          <e2e-confirm-new-map-location-dialog
            (confirmed)="confirmChange(newMarkerLocation())"
            (cancelled)="cancelChange()"
          />
        }

        @if (saving()) {
          <div
            class="column wide absolute top-0 left-0 h-full justify-center z-3 backdrop-blur-sm"
          >
            <div
              class="row justify-center text-8xl text-accent"
            >
              <lib-ui-icon [icon]="faArrowsRotate" animation="spin"/>
            </div>
          </div>
        }
      </div>
      <div>
        You can change the location for your story by dragging the map, zooming in/out, and then clicking where you
        would like to set the new pin location
      </div>
      <e2e-editor-location inherit/>
  `,
  selector: "e2e-editor-map",
})
export class EditorMapComponent {
  private editorService = inject(EditorService)
  private routeService = inject(RouteService)

  story = input.required<Story | undefined>()
  zoomLevelInput = input.required<number>()

  saving = this.editorService.saving
  preSaving = this.editorService.preSaving
  confirmingChange = signal(false)
  newMarkerLocation = signal<LngLat | undefined>(undefined)
  private _zoomLevel = signal<number | undefined>(undefined)
  zoomLevel = computed<number>(() => this._zoomLevel() || this.zoomLevelInput())

  regionDatum = computed(() =>
    regionsData[regionsData[this.routeService.region()].redliningRegion]
  )

  changeMarkerLocation(markerLocation: LngLat) {
    if (!this.routeService.sectionFlags().MODERATION) {
      this.confirmingChange.set(true)
      this.newMarkerLocation.set(markerLocation)
    }
  }

  cancelChange() {
    this.confirmingChange.set(false)
    this.newMarkerLocation.set(undefined)
  }

  confirmChange(markerLocation: LngLat | undefined) {
    const content = this.editorService.editorContent()
    if (content && markerLocation) {
      this.editorService.saveEditor({
        ...content,
        settings: {
          ...content.settings,
          location: {
            ...content.settings.location,
            lat: markerLocation.lat,
            lng: markerLocation.lng,
          },
        },
      })
      this.confirmingChange.set(false)
      this.newMarkerLocation.set(undefined)
    }
  }

  setZoomLevel(zoomLevel: number) {
    this._zoomLevel.set(zoomLevel)
  }

  protected readonly faArrowsRotate = faArrowsRotate
}
